import type { ReactNode } from 'react';
import { LEGAL } from '../lib/legal.js';

/**
 * Os textos legais vêm dos documentos revisados (`docs/legal`), copiados para
 * `src/legal/*.md`. Este componente mostra o recorte simples de markdown que
 * eles usam (seções, subtítulos, parágrafos e listas) e preenche os campos
 * entre colchetes com os dados do responsável em `LEGAL`.
 */
export function fillLegal(text: string): string {
  const name = LEGAL.controllerName ?? `a pessoa responsável pelo ${LEGAL.product}`;
  return (
    text
      // Notas de decisão são para o responsável, não para o app.
      .replace(/\s*\[DECISÃO:(?:[^[\]]|\[[^\]]*\])*\]/g, '')
      // Sem CPF publicado, a menção some junto.
      .replace(/,?\s*CPF \[[^\]]*\]/g, LEGAL.controllerCpf ? `, CPF ${LEGAL.controllerCpf}` : '')
      .replaceAll('[NOME COMPLETO DO ENCARREGADO]', LEGAL.dpoName ?? name)
      .replaceAll('[NOME COMPLETO]', name)
      .replaceAll('[E-MAIL DO ENCARREGADO]', LEGAL.privacyEmail)
      .replaceAll('[CIDADE E UF]', LEGAL.controllerCity ?? 'Brasil')
      .replaceAll(
        '[ENDEREÇO DE CONTATO]',
        LEGAL.controllerAddress ?? `o e-mail ${LEGAL.supportEmail}`,
      )
      .replaceAll('[DATA DE PUBLICAÇÃO]', LEGAL.updatedAt)
      .replaceAll('[DATA]', LEGAL.updatedAt)
  );
}

const EMAIL = /([\w.+-]+@[\w-]+\.[\w.]+[\w])/g;

/** Texto corrido com os e-mails visíveis e selecionáveis. */
function inline(text: string): ReactNode[] {
  return text
    .replace(/\*\*/g, '')
    .split(EMAIL)
    .map((part, index) =>
      index % 2 === 1 ? (
        <a key={index} className="lg-mail" href={`mailto:${part}`}>
          {part}
        </a>
      ) : (
        part
      ),
    );
}

type Block = { kind: 'h2' | 'h3' | 'p'; text: string } | { kind: 'ol' | 'ul'; items: string[] };

export function parseLegal(source: string): Block[] {
  const blocks: Block[] = [];
  for (const raw of fillLegal(source).split('\n')) {
    const line = raw.trim();
    const last = blocks[blocks.length - 1];
    if (!line || line.startsWith('>')) continue;
    if (line.startsWith('### ')) blocks.push({ kind: 'h3', text: line.slice(4) });
    else if (line.startsWith('## ')) blocks.push({ kind: 'h2', text: line.slice(3) });
    else if (/^[-*] /.test(line)) {
      if (last?.kind === 'ul') last.items.push(line.slice(2));
      else blocks.push({ kind: 'ul', items: [line.slice(2)] });
    } else if (/^\d+\. /.test(line)) {
      const item = line.replace(/^\d+\. /, '');
      if (last?.kind === 'ol') last.items.push(item);
      else blocks.push({ kind: 'ol', items: [item] });
    } else blocks.push({ kind: 'p', text: line });
  }
  return blocks;
}

/** Cada "##" abre uma seção, no mesmo desenho das outras páginas legais. */
export function LegalMarkdown({ source }: { source: string }) {
  const sections: Block[][] = [[]];
  for (const block of parseLegal(source)) {
    if (block.kind === 'h2' && sections[sections.length - 1]!.length > 0) sections.push([]);
    sections[sections.length - 1]!.push(block);
  }
  return (
    <>
      {sections.map((blocks, index) => (
        <section key={index}>
          {blocks.map((block, position) => {
            switch (block.kind) {
              case 'h2':
                return <h2 key={position}>{block.text}</h2>;
              case 'h3':
                return <h3 key={position}>{block.text}</h3>;
              case 'p':
                return <p key={position}>{inline(block.text)}</p>;
              case 'ol':
              case 'ul': {
                const List = block.kind;
                return (
                  <List key={position}>
                    {block.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{inline(item)}</li>
                    ))}
                  </List>
                );
              }
            }
          })}
        </section>
      ))}
    </>
  );
}
